import { useCallback, useEffect, useMemo, useRef } from "react";
import type { StudioActionResult } from "@/components/ai/chat/StudioActionsContext";
import { SAMPLE_BATCH_DOCUMENT, SAMPLE_DOCUMENT } from "@/constants";
import { type ExplorerValue, useExplorer } from "./ExplorerContext";
import { usePipelineStaging } from "./pipelineStaging";
import { TEMPLATE_SAMPLE } from "./pipelineTemplate";
import { useStudioPipeline } from "./StudioPipelineProvider";
import type { StageTabId } from "./stagingModel";
import type { StudioEffect } from "./studioActions";
import { useSuiteDropStaging } from "./TestsNavPanel";
import { usePipelineCode } from "./useAiHelperProps";
import { useRunSuiteTest } from "./useRunSuiteTest";
import { useScriptRun } from "./useScriptRun";

// The Studio side of an action: one applier per resolved effect kind,
// turning it into provider calls and answering with the row's result. A
// run's verdict comes later, as a promise. Every applier is a plain
// function over the same dependency bag, so the table reads as the
// registry of what the helper can make the Studio do.

type Result = StudioActionResult;
type Outcome = Result | Promise<Result>;

interface Deps {
  explorer: ExplorerValue;
  staging: ReturnType<typeof usePipelineStaging>;
  prepareStagesSwitch: ReturnType<
    typeof useStudioPipeline
  >["prepareStagesSwitch"];
  stageSuite: (rowId: string) => void;
  runPipeline: () => Promise<void>;
  runRow: ReturnType<typeof useScriptRun>["runRow"];
  runTests: () => Promise<void> | undefined;
  /** Resolves with the next run output the Studio publishes. */
  nextRunOutcome: () => Promise<Result>;
  /** The pipeline code generated from the stages — a code draft's base. */
  generatedCode: string;
}

type EffectOf<K extends StudioEffect["kind"]> = Extract<
  StudioEffect,
  { kind: K }
>;
type Appliers = {
  [K in StudioEffect["kind"]]: (effect: EffectOf<K>, deps: Deps) => Outcome;
};

const ok = (text: string): Result => ({ ok: true, text });

function addAll(set: ReadonlySet<string>, ids: string[]): Set<string> {
  const next = new Set(set);
  for (const id of ids) next.add(id);
  return next;
}

function removeAll(set: ReadonlySet<string>, ids: string[]): Set<string> {
  const next = new Set(set);
  for (const id of ids) next.delete(id);
  return next;
}

// A stage-bound effect lands on its tab first, as the user would.
function landOn(deps: Deps, tab: StageTabId) {
  if (deps.staging.stageTab !== tab) deps.staging.setStageTab(tab);
}

// Stages → Full is a plain switch; Full → Stages goes through the
// hand-over, which refuses while the code has errors. No "already there"
// short-cut: several actions of one message apply in one tick, before the
// view state re-renders, and both switches are no-ops when the view is
// already right.
async function setView(deps: Deps, view: "stages" | "full"): Promise<Result> {
  if (view === "full") {
    deps.staging.setStudioView("full");
    return ok("switched");
  }
  const result = await deps.prepareStagesSwitch();
  return result.kind === "errors"
    ? { ok: false, text: "fix the code errors first" }
    : ok("switched");
}

// A select lands on the row's tab; a test opens in the viewer, a stage
// row through the nav bar's own toggle (skipped when already selected —
// the toggle would hand the selection to the pseudo-row).
function select(deps: Deps, effect: EffectOf<"select">): Result {
  landOn(deps, effect.tab);
  if (effect.test) deps.staging.openTestViewer(effect.id);
  else if (deps.staging.effectiveSelectedId !== effect.id) {
    deps.staging.toggleSelected(effect.id);
  }
  return ok("selected");
}

// A run's row waits for the verdict the Output panel gets.
function run(deps: Deps, start: () => unknown): Promise<Result> {
  const outcome = deps.nextRunOutcome();
  void start();
  return outcome;
}

const SAMPLES: Record<"document" | "batch", string> = {
  document: SAMPLE_DOCUMENT,
  batch: SAMPLE_BATCH_DOCUMENT,
};

function inputText(effect: EffectOf<"input.set">): string {
  return effect.source === "text"
    ? (effect.text ?? "")
    : SAMPLES[effect.source];
}

const APPLIERS: Appliers = {
  "explorer.expand": (e, d) => {
    d.explorer.expandItems(e.items);
    d.explorer.setCollapsedSections((prev) => removeAll(prev, e.sections));
    return ok("shown");
  },
  "explorer.collapse": (e, d) => {
    d.explorer.collapseItems(e.items);
    d.explorer.setCollapsedSections((prev) => addAll(prev, e.sections));
    return ok("collapsed");
  },
  "explorer.filter": (e, d) => {
    d.explorer.setFilterText(e.text);
    return ok(e.text === "" ? "cleared" : "filtered");
  },
  "stage.resource": (e, d) => {
    landOn(d, e.tab);
    d.staging.stage(e.role, e.resource);
    return ok(`staged as ${e.role}`);
  },
  "stage.schema": (e, d) => {
    landOn(d, e.tab);
    d.staging.stageSchema(e.resource, e.tab);
    return ok("staged");
  },
  "stage.suite": (e, d) => {
    landOn(d, e.tab);
    d.stageSuite(e.rowId);
    return ok("staged");
  },
  "tab.set": (e, d) => {
    d.staging.setStageTab(e.tab);
    return ok("switched");
  },
  "view.set": (e, d) => setView(d, e.view),
  select: (e, d) => select(d, e),
  "output.set": (e, d) => {
    d.staging.setOutputCollapsed(e.collapsed);
    return ok(e.collapsed ? "hidden" : "shown");
  },
  "run.pipeline": (_e, d) => run(d, d.runPipeline),
  "run.script": (e, d) => run(d, () => d.runRow(e.resource, e.schema)),
  "run.tests": (_e, d) => run(d, d.runTests),
  unstage: (e, d) => {
    landOn(d, e.tab);
    if (e.untitled) d.staging.discardScriptDraft(e.id);
    d.staging.unstage(e.id);
    return ok("removed");
  },
  reorder: (e, d) => {
    landOn(d, "transform");
    d.staging.reorderTransforms(e.id, e.targetId, e.edge);
    return ok("moved");
  },
  "schema.attach": (e, d) => {
    landOn(d, e.tab);
    d.staging.stageSchemaFor(e.resource, e.target);
    return ok(e.replaces ? `attached, replacing ${e.replaces}` : "attached");
  },
  "schema.detach": (e, d) => {
    landOn(d, e.tab);
    d.staging.detachSchema(e.schemaId, e.target);
    return ok("detached");
  },
  "input.set": (e, d) => {
    landOn(d, "source");
    d.staging.setInputDocument(inputText(e));
    return ok("input replaced");
  },
  "input.load": (e, d) => {
    landOn(d, "source");
    d.staging.showSourceSample(e.resource);
    return ok(`loading a sample of ${e.resource.name}`);
  },
  "script.template": (e, d) => {
    landOn(d, e.tab);
    d.staging.setScriptDocument(e.script);
    return ok("script replaced");
  },
  "pipeline.template": (_e, d) => {
    d.staging.setPipelineCodeDraft({
      text: TEMPLATE_SAMPLE.content,
      basedOn: d.generatedCode,
    });
    return ok("template inserted");
  },
};

// A run reports through the Output panel: the row's verdict is the next
// run output the Studio publishes after the run was started.
function useNextRunOutcome(): () => Promise<Result> {
  const { scriptRunOutput } = usePipelineStaging();
  const waiting = useRef<Array<(r: Result) => void>>([]);
  const seen = useRef(scriptRunOutput);

  useEffect(() => {
    if (scriptRunOutput === seen.current) return;
    seen.current = scriptRunOutput;
    if (!scriptRunOutput) return;
    const resolvers = waiting.current;
    waiting.current = [];
    const text = scriptRunOutput.success
      ? "ran — see the Output panel"
      : "failed — see the Output panel";
    for (const resolve of resolvers) {
      resolve({ ok: scriptRunOutput.success, text });
    }
  }, [scriptRunOutput]);

  return useCallback(
    () =>
      new Promise<Result>((resolve) => {
        waiting.current.push(resolve);
      }),
    [],
  );
}

function useDeps(): Deps {
  const explorer = useExplorer();
  const staging = usePipelineStaging();
  const { prepareStagesSwitch } = useStudioPipeline();
  const suite = useSuiteDropStaging();
  const scriptRun = useScriptRun();
  const suiteRun = useRunSuiteTest();
  const nextRunOutcome = useNextRunOutcome();
  const { generated: generatedCode } = usePipelineCode();
  return useMemo(
    () => ({
      explorer,
      staging,
      prepareStagesSwitch,
      stageSuite: suite.request,
      runPipeline: scriptRun.runPipeline,
      runRow: scriptRun.runRow,
      runTests: suiteRun.runAll,
      nextRunOutcome,
      generatedCode,
    }),
    [
      explorer,
      staging,
      prepareStagesSwitch,
      suite.request,
      scriptRun.runPipeline,
      scriptRun.runRow,
      suiteRun.runAll,
      nextRunOutcome,
      generatedCode,
    ],
  );
}

export function useApplyEffect(): (effect: StudioEffect) => Outcome {
  const deps = useDeps();
  return useCallback(
    (effect) => {
      const apply = APPLIERS[effect.kind] as (
        e: StudioEffect,
        d: Deps,
      ) => Outcome;
      return apply(effect, deps);
    },
    [deps],
  );
}
